import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Text } from 'rebass/styled-components';
import styled from 'styled-components';
import theme from 'styled-theming';
import { StatefulToolTip } from 'react-portal-tooltip';
import SkillsSelector from './SkillsSelector';
import { ReactComponent as Icon } from '../assets/icon-warn.svg';

import {
  Toggle,
  Checkbox,
} from '.';

const textColor = theme('mode', {
  light: (props) => props.theme.colors.deepGrey,
  dark: (props) => props.theme.colors.greyLight,
});

const automationNameColor = theme('mode', {
  light: (props) => props.theme.colors.deepGrey,
  dark: (props) => props.theme.colors.whiteGrey,
});

const backgroundColor = theme('mode', {
  light: (props) => props.theme.colors.midWhite,
  dark: (props) => props.theme.colors.ghostSuperLight,
});

const selectedBackgroundColor = theme('mode', {
  light: (props) => props.theme.colors.midWhite,
  dark: (props) => props.theme.colors.brightCeruleanLight,
});

const AutomationItemWrapper = styled(Flex)`
  position: relative;
  margin-bottom: 12px;
  transition: .2s all;

  &:before {
    backdrop-filter: blur(8px);
    content: "";
    background-color: ${backgroundColor};
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    border-radius: 5px;
    z-index: 0;
  }

  &.selected {
    background-color: ${selectedBackgroundColor};
  }
`;

const AutomationItemElement = styled(Flex)`
  flex: 0 0 auto;
  color: ${textColor};
  font-size: 14px;
  padding: 9px 16px;
  width: 100%;
  max-width: 900px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AutomationName = styled(Flex)`
  z-index: 1;
  font-size: 16px;
  font-weight: bold;
  color: ${automationNameColor};
  padding-right: 16px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
`;

const AutomationSwitcher = styled(Flex)`
  z-index: 1;

  @media only screen and (max-width: 1024px) {
    flex-direction: column;
  }
`;

const ToggleLabel = styled(Text)`
  color: ${textColor};
`;

const CheckBoxWrapper = styled(Flex)`
  margin-right: 40px;

  @media only screen and (max-width: 1100px) {
    margin-right: 10px;
  }
`;

const IconWarn = styled(Icon)`
  margin-right: 5px;
  color: red;
`;

const tooltipStyle = {
  style: {
    padding: '14px',
    fontSize: '12px',
    width: '300px',
  },
  arrowStyle: {
    borderColor: false,
  },
};

const IconWrapper = <span><IconWarn /></span>;

function AutomationItem(props) {
  const {
    automation,
    themeMode,
    updateAction,
    skillsList,
    isSavingAutomation,
    onSelect,
  } = props;

  function handleEnabled() {
    updateAction({
      ...automation,
      enabled: !automation.enabled,
    });
  }

  function handleCheck() {
    onSelect({
      ...automation,
      checked: !automation.checked,
    });
  }

  function skillsChangeHandler(enabledSkills) {
    updateAction({
      ...automation,
      enabledSkillIds: enabledSkills.map((skill) => String(skill.id)),
    });
  }

  function getSkillsValue() {
    return skillsList.filter(({ id }) => automation.enabledSkillIds.includes(String(id)));
  }

  return (
    <AutomationItemWrapper data-testid="automation-list-item" className={automation.checked ? 'selected' : ''}>
      <AutomationItemElement alignItems="center">
        <Flex alignItems="center" width="100%">

          <CheckBoxWrapper>
            <Checkbox
              checked={automation.checked}
              onChange={handleCheck}
            />
          </CheckBoxWrapper>

          <AutomationSwitcher
            data-testid="automation-list-switcher"
            width={['100%', '40%', '18%']}
          >
            <Flex alignItems="center" flexDirection={['column', 'column', 'column', 'row']}>
              <Toggle
                className={themeMode === 'light' ? null : 'dark-theme'}
                icons={false}
                checked={automation.enabled}
                onChange={handleEnabled}
                data-testid="automation-list-toggle"
              />
              <ToggleLabel
                data-testid="mis-label"
                variant="label"
                ml={2}
                fontSize={1}
                fontWeight={400}
                color={automation.enabled ? 'success' : 'midGrey'}
                onClick={(event) => event.stopPropagation()}
              >
                {`${automation.enabled ? 'enabled' : 'disabled'}`}
              </ToggleLabel>
            </Flex>
          </AutomationSwitcher>

          <AutomationName
            width={['100%', '50%', '30%']}
            title={automation.botName || automation.name}
          >
            {automation.warn && (
              <StatefulToolTip
                parent={IconWrapper}
                position="top"
                arrow="left"
                className="stateful-button"
                tooltipTimeout={100}
                style={tooltipStyle}
              >
                Select this bot, and click Update user credentials to update Conversation
                Orchestrator with the latest credentials for the associated bot user.
              </StatefulToolTip>
            )}
            <span data-testid="automation-list-name">
              {automation.botName || automation.name}
            </span>
          </AutomationName>

          <Box
            data-testid="automation-list-skills-selector"
            width={['90%', '90%', '52%']}
            maxWidth="400px"
          >
            <SkillsSelector
              options={skillsList}
              automation={automation}
              isDisabled={!automation.enabled}
              onChange={skillsChangeHandler}
              value={getSkillsValue()}
              isLoading={isSavingAutomation}
            />
          </Box>
        </Flex>
      </AutomationItemElement>
    </AutomationItemWrapper>
  );
}

AutomationItem.defaultProps = {
  isSavingAutomation: [],
};

AutomationItem.propTypes = {
  automation: PropTypes.object.isRequired,
  themeMode: PropTypes.string.isRequired,
  updateAction: PropTypes.func.isRequired,
  skillsList: PropTypes.array.isRequired,
  isSavingAutomation: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
};

export default AutomationItem;
